import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import {
  Container,
  Card,
  InputDiv,
  SvgImage,
  Result,
  MathDiv,
  ActionButtonsRow,
  Select,
  AlertDiv,
  ImageDiv,
  CopyImageButton,
  CopyHTMLButton,
  PageContainer,
  PageHeader,
  Badge,
  InfoCard,
  BackButton,
} from '../../../styles/global'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import { MdError } from 'react-icons/md'
import { FaRegCopy } from 'react-icons/fa6'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'
import { toast } from 'react-toastify'
import { ValidatedInput } from '../../../components/ValidatedInput'
import { Spinner } from '../../../components/Spinner'
import { HistoryNavigation } from '../../../components/HistoryNavigation'
import { LoadingOverlay } from '../../../components/LoadingOverlay'

import { copyResultsToClipboard } from '../../../utils/copyResultsToClipboard'
export function ConcreteWallFoundation() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const [fck, setFck] = useState(20)
  const [fyk, setFyk] = useState(500)
  const [sigma, setSigma] = useState('')
  const [a, setA] = useState('')
  const [cl, setCl] = useState('')
  const [nk, setNk] = useState('')
  const [gammaf, setGammaf] = useState(1.4)
  const [selectedMainRebar, setSelectedMainRebar] = useState('Ø6.3')
  const [selectedSecondaryRebar, setSelectedSecondaryRebar] = useState('Ø5.0')
  const [isFormValid, setIsFormValid] = useState(false)
  const [data, setData] = useState({})
  const [result, setResult] = useState('')
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [history, setHistory] = useState([])
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1)

  const svgRef = useRef(null)

  const HISTORY_KEY = 'concrete-wallfoundation-history'
  const MAX_HISTORY = 10

  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY)
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory)
        setHistory(parsed)
      } catch (error) {
        console.error('Erro ao carregar histórico:', error)
      }
    }
  }, [])

  const saveToHistory = useCallback((calculationData) => {
    const newEntry = {
      ...calculationData,
      timestamp: new Date().toISOString(),
      id: Date.now(),
    }

    setHistory((prevHistory) => {
      const newHistory = [newEntry, ...prevHistory].slice(0, MAX_HISTORY)
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory))
      return newHistory
    })
    setCurrentHistoryIndex(0)
  }, [])

  const loadFromHistory = useCallback(
    (index) => {
      if (index < 0 || index >= history.length) return

      const entry = history[index]
      setFck(entry.fck)
      setFyk(entry.fyk)
      setSigma(entry.sigma)
      setA(entry.a)
      setCl(entry.cl)
      setNk(entry.nk)
      setGammaf(entry.gammaf || 1.4)
      setData(entry.data)
      setSelectedMainRebar(entry.selectedMainRebar)
      setSelectedSecondaryRebar(entry.selectedSecondaryRebar)
      setResult(entry.result)
      setCurrentHistoryIndex(index)
    },
    [history]
  )

  const navigateHistory = useCallback(
    (direction) => {
      const newIndex = currentHistoryIndex + direction
      if (newIndex >= 0 && newIndex < history.length) {
        loadFromHistory(newIndex)
      }
    },
    [currentHistoryIndex, history.length, loadFromHistory]
  )

  const validateField = useCallback((name, value) => {
    if (value === '' || value === null || value === undefined) {
      return 'Campo obrigatório'
    }

    const numValue = Number(value)

    if (isNaN(numValue)) {
      return 'Valor deve ser um número'
    }

    if (numValue <= 0) {
      return 'Valor deve ser maior que 0'
    }

    return null
  }, [])

  useEffect(() => {
    const newErrors = {}

    newErrors.fck = validateField('fck', fck)
    newErrors.fyk = validateField('fyk', fyk)
    newErrors.sigma = validateField('sigma', sigma)
    newErrors.a = validateField('a', a)
    newErrors.cl = validateField('cl', cl)
    newErrors.nk = validateField('nk', nk)
    newErrors.gammaf = validateField('gammaf', gammaf)

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some((error) => error !== null)
    setIsFormValid(!hasErrors)
  }, [fck, fyk, sigma, a, cl, nk, gammaf, validateField])

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }))
  }

  async function handleCalculate(event) {
    event.preventDefault()

    const hasExistingResults = result !== ''

    setIsLoading(true)
    if (hasExistingResults) {
      setIsRecalculating(true)
    }

    try {
      const data = {
        fck,
        fyk,
        sigma,
        a,
        cl,
        nk,
        gammaf,
      }

      setData(data)

      const response = await api.post('/concrete/wall-foundation', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      saveToHistory({
        fck,
        fyk,
        sigma,
        a,
        cl,
        nk,
        gammaf,
        data,
        selectedMainRebar,
        selectedSecondaryRebar,
        result: response.data,
      })

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    } finally {
      setIsLoading(false)
      if (hasExistingResults) {
        setTimeout(() => setIsRecalculating(false), 500)
      }
    }
  }

  function handleCopyResults() {
    copyResultsToClipboard({
      result,
    })
  }

  function handleSave() {
    const stateData = {
      fck,
      fyk,
      sigma,
      a,
      cl,
      nk,
      gammaf,
      isFormValid,
      data,
      result,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'state.json'
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Estado salvo com sucesso!')
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        setFck(jsonData.fck)
        setFyk(jsonData.fyk)
        setSigma(jsonData.sigma)
        setA(jsonData.a)
        setCl(jsonData.cl)
        setNk(jsonData.nk)
        setGammaf(jsonData.gammaf || 1.4)
        setIsFormValid(jsonData.isFormValid)
        setData(jsonData.data)
        setResult(jsonData.result)
        toast.success('Estado carregado com sucesso!')
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
        toast.error('Erro ao carregar o estado')
      }
    }
    reader.readAsText(file)
  }

  const handleCopySVG = async () => {
    const svgElement = svgRef.current
    if (!svgElement) return
    const serializer = new XMLSerializer()
    const svgString = serializer.serializeToString(svgElement)
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)
    const img = new Image()
    img.onload = async () => {
      const canvas = document.createElement('canvas')
      canvas.width = 500
      canvas.height = 300
      const context = canvas.getContext('2d')
      context.drawImage(img, 0, 0)
      canvas.toBlob(async (blob) => {
        if (!blob) return
        try {
          await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })])
          toast.success('Imagem copiada para a área de transferência!')
        } catch (error) {
          console.error('Erro ao copiar a imagem:', error)
          toast.error('Erro ao copiar a imagem')
        }
      }, 'image/png')
      URL.revokeObjectURL(url)
    }
    img.src = url
  }

  const RebarsResult = () => {
    return (
      <SvgImage ref={svgRef} viewBox="0 0 500 300" fill="none" xmlns="http://www.w3.org/2000/svg">
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="start"
        >
          <tspan x="307.077" y="49.8225">
            {result.asdList.find((r) => r.name == selectedSecondaryRebar).qty} {selectedSecondaryRebar}
          </tspan>
        </text>
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="start"
        >
          <tspan x="219.5" y="257.264">
            {selectedMainRebar} c/
            {result.asList.find((r) => r.name == selectedMainRebar).spacing}
          </tspan>
        </text>
        <path d="M69.3471 230.533V269.45H430.653V230.533" stroke="black" strokeWidth="2" />
        <path d="M69.3471 147.707V186.624H430.653V147.707" stroke="black" strokeWidth="2" />
        <path
          d="M55.8942 200.264V137.804L216.368 91.6801H282.671L444.106 137.804V200.264H55.8942Z"
          stroke="black"
          strokeWidth="2"
        />
        <line x1="216.629" y1="91.2128" x2="216.629" y2="39.9424" stroke="black" />
        <line x1="282.06" y1="91.2128" x2="282.06" y2="39.9424" stroke="black" />
        <circle cx="75.2814" cy="180.081" r="4.63875" fill="black" />
        <circle cx="127.284" cy="180.081" r="4.63875" fill="black" />
        <circle cx="191.494" cy="180.081" r="4.63875" fill="black" />
        <circle cx="250" cy="180.081" r="4.63875" fill="black" />
        <circle cx="308.505" cy="180.081" r="4.63875" fill="black" />
        <circle cx="372.716" cy="180.081" r="4.63875" fill="black" />
        <circle cx="424.719" cy="180.081" r="4.63875" fill="black" />
        <line x1="73.3613" y1="179.628" x2="331.422" y2="59.509" stroke="black" />
        <line x1="126.785" y1="180.628" x2="331.378" y2="59.532" stroke="black" />
        <line x1="190.679" y1="180.924" x2="331.307" y2="59.5837" stroke="black" />
        <line x1="249.918" y1="180.29" x2="331.219" y2="59.6828" stroke="black" />
        <line x1="307.704" y1="180.719" x2="331.142" y2="59.8671" stroke="black" />
        <line x1="372.665" y1="180.488" x2="331.16" y2="60.1253" stroke="black" />
        <line x1="424.502" y1="179.657" x2="331.239" y2="60.2701" stroke="black" />
      </SvgImage>
    )
  }

  return (
    <PageContainer>
      <PageHeader $gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
        <div className="header-content">
          <BackButton to="/concreto">
            <FiArrowLeft /> Voltar para Concreto
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/concreto">Concreto</a>
            <span>/</span>
            <span>Fundação de Parede</span>
          </div>
          <h1>🏗️ Fundação sob Paredes em Concreto Armado</h1>
          <p className="subtitle">Dimensionamento de sapatas corridas para fundações sob paredes de concreto armado</p>
          <div className="badges">
            <Badge>📋 NBR 6118:2014</Badge>
            <Badge>🏛️ Fundações Rasas</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #f3f0ff 0%, #e9e0ff 100%)"
        $border="2px solid #d4c5f9"
        $primaryColor="#764ba2"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta Calculadora</h3>
            <p>
              Esta ferramenta realiza o dimensionamento completo de fundações rasas do tipo sapata corrida sob paredes
              de concreto armado, considerando as prescrições da NBR 6118:2014.
            </p>
            <p>
              O cálculo determina as dimensões da fundação (largura e altura), verifica a tensão no concreto e fornece
              as armaduras necessárias (principal e secundária) para resistir aos esforços aplicados.
            </p>
            <div className="norms">
              <span>NBR 6118:2014</span>
              <span>NBR 6122:2019</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          {history.length > 0 && (
            <HistoryNavigation
              currentIndex={currentHistoryIndex}
              totalItems={history.length}
              onNavigate={navigateHistory}
              currentTimestamp={currentHistoryIndex >= 0 ? history[currentHistoryIndex]?.timestamp : null}
            />
          )}
          <form onSubmit={handleCalculate}>
            <h3>Resistências</h3>
            <InputDiv>
              <span>Resistência Característica do Concreto (MPa)</span>
              <ValidatedInput
                name="fck"
                type="number"
                value={fck || ''}
                onChange={(event) => setFck(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Resistência Característica do Aço (MPa)</span>
              <ValidatedInput
                name="fyk"
                type="number"
                value={fyk || ''}
                onChange={(event) => setFyk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Tensão de Admissível do Solo (kN/cm²)</span>
              <ValidatedInput
                name="sigma"
                type="number"
                value={sigma || ''}
                onChange={(event) => setSigma(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Geometria</h3>
            <InputDiv>
              <span>Espessura da parede (cm)</span>
              <ValidatedInput
                name="a"
                type="number"
                value={a || ''}
                onChange={(event) => setA(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Distância da barra tracionada à face tracionada (cm)</span>
              <ValidatedInput
                name="cl"
                type="number"
                value={cl || ''}
                onChange={(event) => setCl(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <h3>Esforços</h3>
            <InputDiv>
              <span>Vertical Característica (kN/cm)</span>
              <ValidatedInput
                name="nk"
                type="number"
                value={nk || ''}
                onChange={(event) => setNk(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <InputDiv>
              <span>Coeficiente de ponderação γf</span>
              <ValidatedInput
                name="gammaf"
                type="number"
                value={gammaf || ''}
                onChange={(event) => setGammaf(Number(event.target.value))}
                errors={errors}
                touched={touched}
                onBlur={handleBlur}
                disabled={isLoading}
              />
            </InputDiv>
            <ActionButtonsRow>
              <button type="button" onClick={handleSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || isLoading || !canCalculate}>
                {isLoading ? (
                  <>
                    <Spinner size="16px" />
                    Calculando...
                  </>
                ) : !canCalculate ? (
                  'Limite atingido'
                ) : (
                  'Calcular'
                )}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <>
            <Result $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay message="Recalculando..." />}
              <h2>Resultados</h2>
              <h3>Geometria</h3>
              <MathDiv>
                <BlockMath math={`A = ${result.finalWallA.toFixed(0)} \\text{ cm}`} />
                <BlockMath math={`h = ${result.finalH.toFixed(0)} \\text{ cm}`} />
                <BlockMath math={`h_0 = ${result.h0.toFixed(2)} \\text{ cm}`} />
              </MathDiv>
              <h3>Tensão no concreto</h3>
              <MathDiv>
                <BlockMath
                  math={`\\sigma_d = \\dfrac{N_d}{a} = \\dfrac{${result.nd.toFixed(2)}}{${data.a}} \\approx ${result.sigmad.toFixed(4)} \\text{ kN/cm²}`}
                />
                {result.check ? (
                  <>
                    <BlockMath math={`\\sigma_d \\leq 0,2 \\cdot f_{cd}`} />
                  </>
                ) : (
                  <AlertDiv>
                    <BlockMath math={`\\sigma_d > 0,2 \\cdot f_{cd}`} />
                    <MdError />
                  </AlertDiv>
                )}
              </MathDiv>
              {result.check && (
                <>
                  <h3>Armação</h3>
                  <MathDiv>
                    <BlockMath
                      math={`A_s = \\dfrac{${result.nd.toFixed(2) * 100} \\cdot (${result.finalWallA}-${data.a})}{8 \\cdot ${result.d} \\cdot ${result.fyd.toFixed(2)}} = ${result.as.toFixed(2)} \\text{ cm²}`}
                    />
                  </MathDiv>
                  <MathDiv>
                    <h4>Armação principal</h4>
                    <InputDiv>
                      <p>Seleção da bitola</p>
                      <Select
                        id="mainRebarSelect"
                        value={selectedMainRebar}
                        onChange={(event) => setSelectedMainRebar(event.target.value)}
                      >
                        {result.asList.map((rebar, index) => (
                          <option key={index} value={rebar.name}>
                            {rebar.name}
                          </option>
                        ))}
                      </Select>
                    </InputDiv>
                    <p>
                      Será necessário barras de {selectedMainRebar} c/
                      {result.asList.find((r) => r.name == selectedMainRebar).spacing}
                    </p>
                  </MathDiv>
                  <MathDiv>
                    <h4>Armação secundária</h4>
                    <InputDiv>
                      <p>Seleção da bitola</p>
                      <Select
                        id="secondaryRebarSelect"
                        value={selectedSecondaryRebar}
                        onChange={(event) => setSelectedSecondaryRebar(event.target.value)}
                      >
                        {result.asdList.map((rebar, index) => (
                          <option key={index} value={rebar.name}>
                            {rebar.name}
                          </option>
                        ))}
                      </Select>
                    </InputDiv>
                    <p>
                      Será necessário {result.asdList.find((r) => r.name == selectedSecondaryRebar).qty} barras de{' '}
                      {selectedSecondaryRebar}
                    </p>
                  </MathDiv>
                  <ImageDiv>
                    <RebarsResult />
                    <CopyImageButton title="Clique para copiar essa imagem" onClick={handleCopySVG}>
                      <FaRegCopy />
                      Copiar
                    </CopyImageButton>
                  </ImageDiv>
                </>
              )}
            </Result>
            <Result className="results-section" $isRecalculating={isRecalculating}>
              {isRecalculating && <LoadingOverlay />}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <h2>Cálculos</h2>
                <CopyHTMLButton type="button" onClick={handleCopyResults}>
                  📋 Copiar Cálculos
                </CopyHTMLButton>
              </div>
              <h3>Geometria</h3>
              <MathDiv>
                <BlockMath math={`A = \\dfrac{N_k}{\\sigma_{adm}}`} />
                <BlockMath math={`A = \\dfrac{${data.nk}}{${data.sigma}} \\approx ${result.wallA}`} />
                <BlockMath math={`\\boxed{A = ${result.finalWallA} \\text{ cm}}`} />
                <BlockMath
                  math={`h \\geq \\dfrac{A-a}{4} = \\dfrac{${result.finalWallA} - ${data.a}}{4} \\approx ${result.h}`}
                />
                <BlockMath math={`\\boxed{h = ${result.finalH} \\text{ cm}}`} />
                <BlockMath>{String.raw`h_0 \geq \left\{ \begin{array}{l} \dfrac{h}{3} \\ 20 \end{array} \right.`}</BlockMath>
                <BlockMath math={`\\boxed{h_0 = ${result.h0.toFixed(2)} \\text{ cm}}`} />
              </MathDiv>
              <h3>Tensão no concreto</h3>
              <MathDiv>
                <BlockMath math={`N_d = ${data.gammaf} \\cdot N_k = ${result.nd.toFixed(2)} \\text{ kN/cm}`} />
                <BlockMath
                  math={`\\sigma_d = \\dfrac{N_d}{a} = \\dfrac{${result.nd.toFixed(2)}}{${data.a}} \\approx ${result.sigmad.toFixed(4)} \\text{ kN/cm²}`}
                />
                {result.check ? (
                  <>
                    <BlockMath math={`\\sigma_d \\leq 0,2 \\cdot f_{cd}`} />
                  </>
                ) : (
                  <AlertDiv>
                    <BlockMath math={`\\sigma_d > 0,2 \\cdot f_{cd}`} />
                    <MdError />
                  </AlertDiv>
                )}
              </MathDiv>
              {result.check && (
                <>
                  <h3>Armação</h3>
                  <MathDiv>
                    <BlockMath math={`Z=d=h-d'`} />
                    <BlockMath math={`Z=${result.finalH}-${data.cl}= ${result.d} \\text{ cm}`} />
                    <BlockMath math={`f_{yd}= \\dfrac{f_{yk}}{1.15} = ${result.fyd.toFixed(2)} \\text{ kN/cm²}`} />
                    <BlockMath math={`A_s = \\dfrac{N_d \\cdot (A-a)}{8 \\cdot Z \\cdot f_{yd}}`} />
                    <BlockMath
                      math={`A_s = \\dfrac{${result.nd.toFixed(2) * 100} \\cdot (${result.finalWallA}-${data.a})}{8 \\cdot ${result.d} \\cdot ${result.fyd.toFixed(2)}} = ${result.as.toFixed(2)} \\text{ cm²}`}
                    />
                  </MathDiv>
                  <MathDiv>
                    <h4>Armação principal</h4>
                    <InputDiv>
                      <p>Seleção da bitola</p>
                      <Select
                        id="mainRebarSelect"
                        value={selectedMainRebar}
                        onChange={(event) => setSelectedMainRebar(event.target.value)}
                      >
                        {result.asList.map((rebar, index) => (
                          <option key={index} value={rebar.name}>
                            {rebar.name}
                          </option>
                        ))}
                      </Select>
                    </InputDiv>
                    <p>
                      Será necessário barras de {selectedMainRebar} c/
                      {result.asList.find((r) => r.name == selectedMainRebar).spacing}
                    </p>
                  </MathDiv>
                  <MathDiv>
                    <h4>Armação secundária</h4>
                    <InputDiv>
                      <p>Seleção da bitola</p>
                      <Select
                        id="secondaryRebarSelect"
                        value={selectedSecondaryRebar}
                        onChange={(event) => setSelectedSecondaryRebar(event.target.value)}
                      >
                        {result.asdList.map((rebar, index) => (
                          <option key={index} value={rebar.name}>
                            {rebar.name}
                          </option>
                        ))}
                      </Select>
                    </InputDiv>
                    <p>
                      Será necessário {result.asdList.find((r) => r.name == selectedSecondaryRebar).qty} barras de{' '}
                      {selectedSecondaryRebar}
                    </p>
                  </MathDiv>
                  <ImageDiv>
                    <RebarsResult />
                    <CopyImageButton title="Clique para copiar essa imagem" onClick={handleCopySVG}>
                      <FaRegCopy />
                      Copiar
                    </CopyImageButton>
                  </ImageDiv>
                </>
              )}
            </Result>
          </>
        )}
      </Container>
    </PageContainer>
  )
}
